import { useCallback } from 'react';

import {
  LeadSavedViewsQuery,
  useDeleteLeadSavedViewMutation,
  useLeadSavedViewsQuery,
  useSaveLeadSavedViewMutation,
} from 'core/graphql';

import { LeadsView } from './leadsBoardModel';
import { ILeadsFilters } from './leadsModel';
import { leadsFiltersToView } from './leadsViewsModel';
import { useLeadPermissions } from './useLeadPermissions';

/** ТЗ 0120 — сохранённые виды «Лидов»: свои и общие (общие ведёт Admin). */

export type ILeadSavedView = LeadSavedViewsQuery['leadSavedViews'][number];

const NO_VIEWS: ILeadSavedView[] = [];

export interface ILeadViewWriteResult {
  ok: boolean;
  /** `customErrors.code` или `NETWORK`. */
  code: string | null;
  message: string | null;
}

const REFETCH = ['LeadSavedViews'];

/**
 * Реестр видов: `scope` отделяет виды «Лидов» от видов «Договоров»
 * (ТЗ 0120, этап 2) — и для чтения, и для записи.
 */
const VIEWS_SCOPE = { scope: 'leads' };

export const useLeadSavedViews = (enabled = true) => {
  const { canUse, isAdmin } = useLeadPermissions();
  const { data, loading } = useLeadSavedViewsQuery({
    variables: VIEWS_SCOPE,
    skip: !canUse || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const [saveMut, { loading: saving }] = useSaveLeadSavedViewMutation();
  const [deleteMut, { loading: deleting }] = useDeleteLeadSavedViewMutation();

  const save = useCallback(
    async (input: {
      name: string;
      filters: ILeadsFilters;
      view: LeadsView;
      shared: boolean;
    }): Promise<ILeadViewWriteResult> => {
      try {
        const { data: res } = await saveMut({
          variables: {
            ...VIEWS_SCOPE,
            name: input.name.trim(),
            filters: leadsFiltersToView(input.filters),
            view: input.view,
            shared: isAdmin && input.shared,
          },
          refetchQueries: REFETCH,
          awaitRefetchQueries: true,
        });
        const payload = res?.saveLeadSavedView;
        const error = payload?.customErrors?.find(Boolean);
        if (payload?.success && !error) {
          return { ok: true, code: null, message: null };
        }
        return {
          ok: false,
          code: error?.code || 'ERROR',
          message: error?.message || null,
        };
      } catch (caught) {
        return { ok: false, code: 'NETWORK', message: null };
      }
    },
    [isAdmin, saveMut],
  );

  const remove = useCallback(
    async (id: string): Promise<ILeadViewWriteResult> => {
      try {
        const { data: res } = await deleteMut({
          variables: { ...VIEWS_SCOPE, id },
          refetchQueries: REFETCH,
          awaitRefetchQueries: true,
        });
        const payload = res?.deleteLeadSavedView;
        const error = payload?.customErrors?.find(Boolean);
        if (payload?.success && !error) {
          return { ok: true, code: null, message: null };
        }
        return {
          ok: false,
          code: error?.code || 'ERROR',
          message: error?.message || null,
        };
      } catch (caught) {
        return { ok: false, code: 'NETWORK', message: null };
      }
    },
    [deleteMut],
  );

  const views = data?.leadSavedViews ?? NO_VIEWS;
  return {
    views,
    loading: loading && !data,
    /** Общий вид создаёт и удаляет только Admin. */
    canShare: isAdmin,
    save,
    remove,
    busy: saving || deleting,
  };
};
